import React, { Suspense, useState } from "react";
import { Maximize2, X, Route, Bus, MapPin, School } from "lucide-react";

const GoogleMaps = React.lazy(() =>
  import("../../modules/TrackingControls/TrackingControlsUpdate2")
);

const mapFallback = (
  <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
    Chargement de la carte...
  </div>
);

const TrackingPageLite2 = ({
  isExpanded = false,
  onExpand,
  onClose,
  embeddedMode = false,
  mapControlScope,
  enableFleetVehicleOverlay = false,
  showPickupPoints,
  showTrip,
  showSchools,
  showVehicles,
  schools,
  tripData,
  pickupPoints,
  areaDataSource,
}) => {
  const [visibleLayers, setVisibleLayers] = useState({
    pickup: true,
    trip: true,
    school: true,
    bus: true,
  });

  const toggleLayer = (layerKey) => {
    setVisibleLayers((prev) => ({
      ...prev,
      [layerKey]: !prev[layerKey],
    }));
  };

  const getLayerButtonClassName = (isActive, activeClassName) =>
    `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium shadow-sm transition-colors ${
      isActive
        ? `${activeClassName} text-white`
        : "bg-white text-[#5d6778] ring-1 ring-inset ring-[#d7dce3]"
    }`;

  const mapProps = {
    primaryChecked: false,
    analyticsPeriod: null,
    mapControlScope,
    enableFleetVehicleOverlay,
    showPickupPoints: showPickupPoints ?? visibleLayers.pickup,
    showTrip: showTrip ?? visibleLayers.trip,
    showSchools: showSchools ?? visibleLayers.school,
    showVehicles: showVehicles ?? visibleLayers.bus,
    schools,
    tripData,
    pickupPoints,
    areaDataSource,
    alertsList: <div />,
    periodSelector: <div />,
  };

  const mapContent = (
    <Suspense fallback={mapFallback}>
      <GoogleMaps {...mapProps} />
    </Suspense>
  );

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-[150] bg-background">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
            <h3 className="text-lg font-semibold text-foreground">Suivi en Temps Reel</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary/60"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">{mapContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        embeddedMode
          ? "h-full"
          : "flex h-full flex-col rounded-2xl border border-border bg-card p-4 shadow-sm"
      }
    >
      {!embeddedMode && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className="shrink-0 text-base font-semibold tracking-tight text-foreground md:text-lg">
              Suivi en Temps Reel:
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => toggleLayer("pickup")}
                className={getLayerButtonClassName(visibleLayers.pickup, "bg-[#1db879]")}
              >
                <MapPin className="h-3.5 w-3.5" />
                <span>Ramassage</span>
              </button>
              <button
                type="button"
                onClick={() => toggleLayer("trip")}
                className={getLayerButtonClassName(visibleLayers.trip, "bg-[#8b5cf6]")}
              >
                <Route className="h-3.5 w-3.5" />
                <span>Trajet</span>
              </button>
              <button
                type="button"
                onClick={() => toggleLayer("school")}
                className={getLayerButtonClassName(visibleLayers.school, "bg-[#f59e0b]")}
              >
                <School className="h-3.5 w-3.5" />
                <span>Ecole</span>
              </button>
              <button
                type="button"
                onClick={() => toggleLayer("bus")}
                className={getLayerButtonClassName(visibleLayers.bus, "bg-[#2563eb]")}
              >
                <Bus className="h-3.5 w-3.5" />
                <span>Bus</span>
              </button>
            </div>
          </div>
          <button
            type="button"
            className="rounded p-1.5 hover:bg-secondary"
            onClick={onExpand}
          >
            <Maximize2 className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      )}

      <div
        className={
          embeddedMode
            ? "relative h-full overflow-hidden"
            : "relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-secondary/20"
        }
      >
        {mapContent}
      </div>
    </div>
  );
};

export default TrackingPageLite2;
